import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { View, StyleSheet, Text, Platform, ActivityIndicator, Linking, Alert } from 'react-native';
import { useTheme } from '@/theme';
import { getUserFriendlyErrorMessage } from '@/utils/retry';
import { Caption, Body } from '@/components/ui/Text';
import { Ionicons } from '@expo/vector-icons';
import { MapMarker, createWebMarkerElement } from './MapMarker';
import type { LocationCoordinates } from '@/services/location';
import type { ItineraryItem } from '@/services/itinerary';
import type { Place } from '@/services/places';
import type { MapReferencePoint, WalkingRadiusConfig, ClusterInfo, MapPinDistanceMetadata } from '@/types/mapReferences';

// Mapbox GL JS will be imported dynamically on web to avoid Babel processing issues

import type { RNMapboxModule, RNMapboxCamera } from '@/types/mapbox';
import { isMapboxTokenConfigured } from '@/config/mapbox';
import { isAggressiveMapboxCostMode } from '@/config/mapboxCostPolicy';

/**
 * When `true` on Android only, skips Mapbox `UserLocation` (blue puck) for debugging.
 * Leave `false` in normal builds (default). Turn on only when isolating map vs other AR flows.
 * Search: ANDROID_MAP_AR_DIAGNOSTIC.
 */
const ANDROID_MAP_AR_DIAGNOSTIC = false;

// Only import Mapbox on native platforms (iOS/Android)
let Mapbox: RNMapboxModule | null = null;
let Camera: React.ComponentType<any> | null = null;
let PointAnnotation: React.ComponentType<any> | null = null;
let MarkerView: React.ComponentType<any> | null = null;
let ShapeSource: React.ComponentType<any> | null = null;
let LineLayer: React.ComponentType<any> | null = null;
let MAPBOX_STYLE_URL = '';

if (Platform.OS !== 'web') {
  try {
    const mapboxModule = require('@rnmapbox/maps');
    Mapbox = mapboxModule.default;
    Camera = mapboxModule.Camera;
    PointAnnotation = mapboxModule.PointAnnotation;
    MarkerView = mapboxModule.MarkerView;
    ShapeSource = mapboxModule.ShapeSource;
    LineLayer = mapboxModule.LineLayer;
    const { MAPBOX_STYLE_URL: styleUrl } = require('@/config/mapbox');
    MAPBOX_STYLE_URL = styleUrl;

    // Initialize Mapbox (with error handling)
    try {
      const { getMapboxAccessTokenSync } = require('@/config/mapbox');
      const token = getMapboxAccessTokenSync();
      if (Mapbox && token) {
        Mapbox.setAccessToken(token);
        if (typeof Mapbox.setTelemetryEnabled === 'function') {
          Mapbox.setTelemetryEnabled(false);
        }
      }
    } catch (error) {
      console.warn('[MapView] Mapbox token not configured:', error);
    }
  } catch (error) {
    console.warn('[MapView] Mapbox not available:', error);
  }
}

export type MapPin = {
  id: string;
  /**
   * Trip id this pin belongs to. Used to prevent pins from a previously-selected trip
   * from rendering while the next trip's itinerary/geocoding loads.
   *
   * Not set for user/search pins.
   */
  tripId?: string | null;
  coordinates: LocationCoordinates;
  title: string;
  type: 'itinerary' | 'landmark' | 'search' | 'user';
  category?: ItineraryItem['category'];
  data?: ItineraryItem | Place;
  /**
   * Stable local calendar date key (YYYY-MM-DD) for itinerary pins.
   * This is computed at pin creation time to avoid timezone/day-shift issues and transient missing `data.startDate`.
   */
  dateKey?: string | null;
  /**
   * For multi-city trips: which destination index this pin belongs to.
   * Used to prevent overlap-day pins from showing under the wrong city.
   */
  cityIndex?: number | null;
  /**
   * The raw location text used to geocode this pin (address or location string).
   * Useful for debugging mis-geocodes and overlap/travel-day disambiguation.
   */
  locationText?: string | null;
  index?: number; // Optional index for numbered markers (e.g., city order)
  subtitle?: string; // Optional subtitle for popup (e.g., country, dates)
  badges?: string[];
  highlight?: 'must_see' | 'trending';
  distanceMeta?: MapPinDistanceMetadata & { distanceMeters?: number };
  /** When set, overrides the default color from `pin.type` (e.g. multi-user live share). */
  markerColor?: string;
};

export type MapBounds = {
  southWest: LocationCoordinates;
  northEast: LocationCoordinates;
};

export type RouteLine = {
  id: string;
  coordinates: LocationCoordinates[];
  color?: string;
  width?: number;
  distance?: number; // Distance in kilometers
  duration?: number; // Duration in minutes
  label?: string; // Optional custom label
};

export type RouteArrow = {
  id: string;
  coordinate: LocationCoordinates;
  bearingDeg: number; // 0 = north, 90 = east
  color?: string;
  size?: number;
};

type MapViewProps = {
  pins: MapPin[];
  center?: LocationCoordinates;
  zoomLevel?: number;
  minZoomLevel?: number;
  showUserLocation?: boolean;
  /**
   * Whether numbered order chips should render for pins that have `pin.index`.
   * Useful to disable in "All days" mode to avoid stale/irrelevant numbering.
   */
  showIndexChips?: boolean;
  onPinPress?: (pin: MapPin) => void;
  onMapPress?: (coordinates: LocationCoordinates) => void;
  onZoomChange?: (zoom: number) => void;
  mapStyle?: string; // Mapbox style URL
  routes?: RouteLine[];
  routeArrows?: RouteArrow[];
  style?: any;
  bounds?: MapBounds | null;
  boundsPadding?: number | {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
  calloutPinId?: string | null;
  renderCallout?: (pin: MapPin, onClose?: () => void) => React.ReactNode;
  onCloseCallout?: () => void;
  primaryReferencePoint?: MapReferencePoint | null;
  walkingRadiusConfig?: WalkingRadiusConfig;
  clusters?: ClusterInfo[];
  showWebNavigationControls?: boolean;
  zoomAdjustment?: { nonce: number; delta: number } | null;
  /** Milliseconds for programmatic fitBounds. Use 0 while the user hasn’t touched the map to avoid a visible “city view then zoom in” jolt. */
  boundsAnimationDurationMs?: number;
};

export const MapView: React.FC<MapViewProps> = ({
  pins,
  center,
  zoomLevel = 12,
  minZoomLevel = 4,
  showUserLocation = true,
  showIndexChips = true,
  onPinPress,
  onMapPress,
  onZoomChange,
  mapStyle,
  routes = [],
  routeArrows = [],
  style,
  bounds,
  boundsPadding = 40,
  calloutPinId,
  showWebNavigationControls = false,
  zoomAdjustment = null,
  boundsAnimationDurationMs = 700,
}) => {
  const theme = useTheme();
  const showNativeUserLocationDot =
    showUserLocation && !(ANDROID_MAP_AR_DIAGNOSTIC && Platform.OS === 'android');
  const cameraRef = useRef<RNMapboxCamera | null>(null);
  const [mapboxError, setMapboxError] = React.useState<string | null>(null);
  const zoomRef = useRef<number>(zoomLevel);
  const zoomCommandNonceRef = useRef<number | null>(null);
  const zoomEmitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingZoomRef = useRef<number>(zoomLevel);
  // Tracks the last *programmatic* center we applied. We intentionally do not
  // "control" zoom via props to avoid feedback loops with `onCameraChanged`.
  const lastAppliedCenterKeyRef = useRef<string | null>(null);
  // When we move/zoom the camera programmatically, Mapbox emits `onCameraChanged`.
  // If we forward that event upstream immediately, it can create a render loop:
  // setCamera -> onCameraChanged -> setState -> props change -> setCamera -> ...
  const suppressZoomEventsUntilRef = useRef<number>(0);
  const PROGRAMMATIC_ZOOM_SUPPRESS_MS = 1200;
  const FIT_BOUNDS_SUPPRESS_WEB_MS = 1500;
  const FIT_BOUNDS_SUPPRESS_NATIVE_MS = 1400;
  // `onCameraChanged` can fire many times per second. Throttle emitted zoom updates to
  // avoid expensive pin filtering + rerenders upstream.
  const lastZoomEmitRef = useRef<{ t: number; z: number }>({ t: 0, z: zoomLevel });

  // Keep zoom reference in sync with prop updates
  useEffect(() => {
    zoomRef.current = zoomLevel;
    pendingZoomRef.current = zoomLevel;
  }, [zoomLevel]);

  useEffect(() => {
    return () => {
      if (zoomEmitTimerRef.current) {
        clearTimeout(zoomEmitTimerRef.current);
        zoomEmitTimerRef.current = null;
      }
    };
  }, []);

  // If multiple itinerary pins share identical coordinates (common when falling back to city center),
  // they overlap visually. Slightly jitter them for readability without changing underlying data.
  const pinsForRender = useMemo(() => {
    const groups = new Map<string, MapPin[]>();
    pins.forEach((pin) => {
      const key = `${pin.coordinates.latitude.toFixed(6)}:${pin.coordinates.longitude.toFixed(6)}:${pin.type}`;
      const list = groups.get(key) ?? [];
      list.push(pin);
      groups.set(key, list);
    });

    const out: MapPin[] = [];
    groups.forEach((group) => {
      if (group.length <= 1) {
        out.push(group[0]);
        return;
      }

      const base = group[0].coordinates;
      const radius = 0.00022; // ~24m
      const step = (Math.PI * 2) / group.length;

      group.forEach((pin, i) => {
        if (pin.type !== 'itinerary') {
          out.push(pin);
          return;
        }
        const angle = i * step;
        out.push({
          ...pin,
          coordinates: {
            latitude: base.latitude + Math.sin(angle) * radius,
            longitude: base.longitude + Math.cos(angle) * radius,
          },
        });
      });
    });

    return out;
  }, [pins]);

  // Draw landmarks first so itinerary markers stay visible when pins overlap.
  const pinsForDrawOrder = useMemo(() => {
    const drawPriority = (pin: MapPin): number => {
      if (pin.type === 'landmark') return 1;
      if (pin.type === 'user' || pin.type === 'search') return 2;
      return 3;
    };
    return [...pinsForRender].sort((a, b) => drawPriority(a) - drawPriority(b));
  }, [pinsForRender]);

  const isUsableCoordinate = useCallback((value?: LocationCoordinates | null) => {
    if (!value) return false;
    if (!Number.isFinite(value.latitude) || !Number.isFinite(value.longitude)) return false;
    if (Math.abs(value.latitude) > 90 || Math.abs(value.longitude) > 180) return false;
    if (value.latitude === 0 && value.longitude === 0) return false;
    return true;
  }, []);

  const resolvedInitialCenter = useMemo<LocationCoordinates | undefined>(() => {
    if (isUsableCoordinate(center)) return center;
    const preferredPin =
      pinsForRender.find((p) => p.type === 'itinerary' && isUsableCoordinate(p.coordinates)) ||
      pinsForRender.find((p) => p.type === 'landmark' && isUsableCoordinate(p.coordinates)) ||
      pinsForRender.find((p) => p.type === 'search' && isUsableCoordinate(p.coordinates)) ||
      pinsForRender.find((p) => isUsableCoordinate(p.coordinates));
    return preferredPin?.coordinates;
  }, [center, isUsableCoordinate, pinsForRender]);

  const nativeDefaultCameraSettings = useMemo(() => {
    const settings: {
      centerCoordinate?: [number, number];
      zoomLevel: number;
    } = {
      zoomLevel: Math.max(minZoomLevel, zoomLevel),
    };
    if (resolvedInitialCenter) {
      settings.centerCoordinate = [resolvedInitialCenter.longitude, resolvedInitialCenter.latitude];
    }
    return settings;
  }, [minZoomLevel, resolvedInitialCenter, zoomLevel]);

  // Force native Mapbox to remount annotations when the pin set changes so pins show
  // without app switch (PointAnnotation/MarkerView often don't update until layout/foreground).
  const pinsListKey = useMemo(() => {
    const ids = pinsForRender.map((p) => p.id).sort().join(',');
    return `pins-${pinsForRender.length}-${ids}`;
  }, [pinsForRender]);

  // Web-specific refs
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  // Web types: use permissive refs because mapbox-gl is dynamically imported and our local types are intentionally minimal.
  const mapRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const popupsRef = useRef<any[]>([]);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [styleLoaded, setStyleLoaded] = useState(false);
  const [containerReady, setContainerReady] = useState(false);
  const initAttemptedRef = useRef(false);

  useEffect(() => {
    if (Platform.OS === 'web') return;
    if (!isMapboxTokenConfigured()) {
      setMapboxError('Mapbox access token not configured. See MAPBOX_SETUP.md for setup instructions.');
    }
  }, []);

  // When bounds are not provided, we still want the map to react to `center`/`zoomLevel` changes
  // (e.g. switching trips). The native Camera defaultSettings are only applied on mount, so we
  // must update the camera explicitly for subsequent prop changes.
  useEffect(() => {
    if (!center || bounds) return;
    if (center.latitude === 0 && center.longitude === 0) return;

    const nextZoom = Math.max(minZoomLevel, zoomLevel);
    // Use high precision so callers can "force recenter" with tiny nudges (e.g. +1e-9).
    const centerKey = `${center.latitude.toFixed(9)},${center.longitude.toFixed(9)}`;
    const prevCenterKey = lastAppliedCenterKeyRef.current;
    // IMPORTANT: do not programmatically re-apply camera settings when only `zoomLevel`
    // changes (typically caused by `onCameraChanged -> onZoomChange` state updates).
    if (prevCenterKey === centerKey) return;
    // If this is the first meaningful center we’ve applied (common when the initial render
    // starts at {0,0} while async destination geocoding loads), avoid a slow animation.
    const isFirstMove = !prevCenterKey;
    lastAppliedCenterKeyRef.current = centerKey;

    if (Platform.OS === 'web') {
      const map = mapRef.current;
      if (!map || !mapLoaded) return;
      try {
        map.easeTo({
          center: [center.longitude, center.latitude],
          zoom: nextZoom,
          duration: 600,
        });
      } catch (error) {
        // Ignore; map might not be ready yet
      }
      return;
    }

    if (!cameraRef.current) return;
    try {
      suppressZoomEventsUntilRef.current = Date.now() + (isFirstMove ? 250 : PROGRAMMATIC_ZOOM_SUPPRESS_MS);
      cameraRef.current.setCamera({
        centerCoordinate: [center.longitude, center.latitude],
        zoomLevel: nextZoom,
        animationDuration: isFirstMove ? 0 : 600,
      });
    } catch (error) {
      // Ignore; camera might not be ready yet
    }
  }, [bounds, center, mapLoaded, minZoomLevel, zoomLevel]);

  // Web map initialization - wait for container ref to be set
  useEffect(() => {
    if (Platform.OS !== 'web') {
      return;
    }

    // Check if map is already initialized
    if (mapRef.current) {
      return;
    }

    // Don't attempt initialization until container is ready
    if (!containerReady || !mapContainerRef.current) {
      // Debug: log why we're not initializing
      if (__DEV__ && Platform.OS === 'web') {
        if (!containerReady) {
          console.log('[MapView] Waiting for container to be ready...');
        }
        if (!mapContainerRef.current) {
          console.log('[MapView] Map container ref not available');
        }
      }
      return;
    }

    // Prevent multiple initialization attempts
    if (initAttemptedRef.current) {
      return;
    }
    initAttemptedRef.current = true;
    
    if (__DEV__ && Platform.OS === 'web') {
      console.log('[MapView] Starting map initialization...');
    }

    // Dynamically import mapbox-gl only on web
    let mapboxgl: any = null;
    const loadMapbox = async () => {
      try {
        // Get the access token FIRST, before importing mapbox-gl
        const { getMapboxAccessTokenSync } = require('@/config/mapbox');
        let accessToken: string;
        
        try {
          accessToken = getMapboxAccessTokenSync();
          if (!accessToken || !accessToken.startsWith('pk.')) {
            throw new Error('Invalid Mapbox access token format. Token should start with "pk."');
          }
          // Token retrieved successfully
        } catch (tokenError) {
          console.error('[MapView] Failed to get Mapbox access token:', tokenError);
          const userMessage = getUserFriendlyErrorMessage(tokenError);
          // Provide more helpful error message with actionable steps
          const errorMsg = userMessage || 'Mapbox token not configured. Please check your configuration.';
          setMapboxError(
            errorMsg.includes('not configured') 
              ? 'Mapbox access token not configured. See MAPBOX_SETUP.md for setup instructions.'
              : errorMsg
          );
          return;
        }

        // Dynamic import to avoid Babel processing issues
        mapboxgl = (await import('mapbox-gl')).default;
        
        // Set the access token IMMEDIATELY after importing
        mapboxgl.accessToken = accessToken;
        
        // Import CSS dynamically - use require for CSS to avoid type errors
        if (typeof window !== 'undefined') {
          require('mapbox-gl/dist/mapbox-gl.css');
        }
      } catch (error) {
        console.error('[MapView] Failed to load Mapbox GL JS:', error);
        const userMessage = getUserFriendlyErrorMessage(error);
        setMapboxError(userMessage || 'Failed to load map library. Please ensure mapbox-gl is installed.');
        return;
      }

      // Container should already be ready at this point, but double-check
      const checkAndInit = () => {
        if (!mapContainerRef.current) {
          // This shouldn't happen since we wait for containerReady, but add a quick fallback
          const attempts = (checkAndInit as any).attempts || 0;
          if (attempts < 5) { // Reduced from 20 to 5 attempts
            (checkAndInit as any).attempts = attempts + 1;
            setTimeout(checkAndInit, 50); // Reduced delay from 100ms to 50ms
          } else {
            console.error('[MapView] Map container ref not available after retries');
            setMapboxError('Failed to initialize map container. Please refresh the page.');
          }
          return;
        }

        // Ensure container has dimensions before initializing map
        const container = mapContainerRef.current;
        if (container) {
          const computedStyle = window.getComputedStyle(container);
          const parent = container.parentElement;
          const parentStyle = parent ? window.getComputedStyle(parent) : null;
          
          // Force explicit dimensions if not set
          let needsRetry = false;
          
          if (!computedStyle.width || computedStyle.width === '0px' || computedStyle.width === 'auto') {
            const width = container.offsetWidth || 
                         (parentStyle?.width && parentStyle.width !== 'auto' ? parseInt(parentStyle.width) : null) ||
                         (parent?.clientWidth || 0);
            if (width > 0) {
              container.style.width = `${width}px`;
            } else {
              container.style.width = '100%';
              needsRetry = true;
            }
          }
          
          if (!computedStyle.height || computedStyle.height === '0px' || computedStyle.height === 'auto') {
            const height = container.offsetHeight || 
                          (parentStyle?.height && parentStyle.height !== 'auto' ? parseInt(parentStyle.height) : null) ||
                          (parent?.clientHeight || 0);
            if (height > 0) {
              container.style.height = `${height}px`;
            } else {
              container.style.height = '300px';
              needsRetry = true;
            }
          }
          
          // Ensure position is relative or absolute
          if (computedStyle.position === 'static') {
            container.style.position = 'relative';
          }
          
          // If dimensions are still 0, retry (but faster)
          if (needsRetry && (container.offsetWidth === 0 || container.offsetHeight === 0)) {
            const retryAttempts = (checkAndInit as any).retryAttempts || 0;
            if (retryAttempts < 3) { // Reduced from 5 to 3
              (checkAndInit as any).retryAttempts = retryAttempts + 1;
              setTimeout(checkAndInit, 50); // Reduced from 200ms to 50ms
              return;
            }
          }
        }

        try {
          // Token should already be set globally, but verify
          if (!mapboxgl.accessToken) {
            const { getMapboxAccessTokenSync } = require('@/config/mapbox');
            mapboxgl.accessToken = getMapboxAccessTokenSync();
          }

          // Use provided mapStyle or get default
          let styleURL: string;
          if (mapStyle) {
            styleURL = mapStyle;
          } else {
            const { getWebStyleURL } = require('@/config/mapbox');
            styleURL = getWebStyleURL(mapboxgl.accessToken);
          }

          const map = new mapboxgl.Map({
            container: mapContainerRef.current,
            style: styleURL,
            center: resolvedInitialCenter ? [resolvedInitialCenter.longitude, resolvedInitialCenter.latitude] : undefined,
            zoom: zoomLevel,
            minZoom: minZoomLevel,
          });

          // Add navigation controls (zoom buttons) only if explicitly enabled (defaults off for cleaner UI)
          if (showWebNavigationControls) {
            const nav = new mapboxgl.NavigationControl({
              showCompass: false,
              showZoom: true,
            });
            map.addControl(nav, 'top-right');
          }

          map.on('load', () => {
            if (__DEV__ && Platform.OS === 'web') {
              console.log('[MapView] Map loaded successfully');
            }
            setMapLoaded(true);
          });

          // Track zoom changes so custom buttons stay in sync.
          // Suppress events right after programmatic fitBounds/flyTo to avoid infinite update loops.
          map.on('zoomend', () => {
            if (Date.now() < suppressZoomEventsUntilRef.current) return;
            const currentZoom = map.getZoom?.();
            if (typeof currentZoom === 'number') {
              zoomRef.current = currentZoom;
              onZoomChange?.(currentZoom);
            }
          });

          // Listen for style load event
          map.on('style.load', () => {
            if (__DEV__ && Platform.OS === 'web') {
              console.log('[MapView] Style loaded');
            }
            setStyleLoaded(true);
          });

          // Check if style is already loaded (in case event fired before listener)
          if (map.isStyleLoaded()) {
            setStyleLoaded(true);
          }
          
          // Also listen for render event as fallback
          map.once('render', () => {
            if (__DEV__ && Platform.OS === 'web') {
              console.log('[MapView] Map rendered');
            }
            // Map is visible even if load event hasn't fired
            if (!mapLoaded) {
              setMapLoaded(true);
            }
            // Check style again on render
            if (map.isStyleLoaded() && !styleLoaded) {
              setStyleLoaded(true);
            }
          });

          map.on('error', (e: any) => {
            const error = e.error || e;
            const msg = (error?.message || '').toString();
            const status = error?.status;
            if (status === 403 || msg.includes('403') || msg.includes('Forbidden')) {
              try {
                const { markMapboxTokenRejected } = require('@/config/mapbox');
                markMapboxTokenRejected();
              } catch (_) {}
              setMapboxError('Mapbox token rejected (403). Check token and URL restrictions. See MAPBOX_SETUP.md.');
              return;
            }
            const userMessage = getUserFriendlyErrorMessage(error);
            // Retry mechanism for transient errors
            if (error?.message?.includes('NetworkError') || 
                error?.message?.includes('Failed to fetch') ||
                error?.status >= 500) {
              // Retry after delay for network/server errors
              setTimeout(() => {
                if (mapRef.current && !mapRef.current.isStyleLoaded()) {
                  // Try to reload the map style
                  try {
                    const currentStyle = mapRef.current.getStyle();
                    if (currentStyle) {
                      mapRef.current.setStyle(currentStyle);
                    }
                  } catch (retryError) {
                    // If retry fails, show error
                    setMapboxError(userMessage);
                  }
                }
              }, 2000);
            } else {
              setMapboxError(userMessage);
            }
          });

          // Handle map clicks on features (POIs, landmarks, etc.)
          map.on('click', (e: any) => {
            // First check if user clicked on one of our markers (don't process if so)
            const clickedMarker = markersRef.current.find(marker => {
              const markerElement = marker.getElement();
              if (!markerElement) return false;
              const rect = markerElement.getBoundingClientRect();
              const mapContainer = mapContainerRef.current;
              if (!mapContainer) return false;
              const containerRect = mapContainer.getBoundingClientRect();
              const clickX = e.point.x;
              const clickY = e.point.y;
              // Check if click is within marker bounds (approximate)
              return clickX >= rect.left - containerRect.left - 20 && 
                     clickX <= rect.right - containerRect.left + 20 &&
                     clickY >= rect.top - containerRect.top - 20 && 
                     clickY <= rect.bottom - containerRect.top + 20;
            });
            
            if (clickedMarker) {
              // User clicked on our marker, let the marker handler deal with it
              return;
            }
            
            // Check if user clicked on a map feature (POI, landmark, etc.)
            const features = map.queryRenderedFeatures(e.point, {
              layers: [], // Query all layers
            });
            
            console.log('[MapView] Map clicked, found features:', features.length);
            if (features.length > 0) {
              console.log('[MapView] Feature details:', features.map((f: any) => ({
                layer: f.layer?.id,
                sourceLayer: f.sourceLayer,
                properties: f.properties,
              })));
            }
            
            // Look for POI features or place labels - try multiple strategies
            const poiFeature = features.find((f: any) => {
              const layerId = f.layer?.id || '';
              const sourceLayer = f.sourceLayer || '';
              const props = f.properties || {};
              
              // Check layer IDs
              if (layerId.includes('poi') || layerId.includes('place') || 
                  layerId.includes('label') || layerId.includes('symbol')) {
                return true;
              }
              
              // Check source layers
              if (sourceLayer.includes('poi') || sourceLayer.includes('place') || 
                  sourceLayer.includes('label')) {
                return true;
              }
              
              // Check properties
              if (props.name || props.place_name || props.text || 
                  props.poi || props.category || props.maki) {
                return true;
              }
              
              return false;
            });
            
            if (poiFeature && poiFeature.geometry) {
              let coordinates;
              
              // Handle different geometry types
              if (poiFeature.geometry.type === 'Point' && poiFeature.geometry.coordinates) {
                coordinates = {
                  latitude: poiFeature.geometry.coordinates[1],
                  longitude: poiFeature.geometry.coordinates[0],
                };
              } else if (poiFeature.geometry.coordinates && Array.isArray(poiFeature.geometry.coordinates[0])) {
                // MultiPoint or LineString - use first coordinate
                coordinates = {
                  latitude: poiFeature.geometry.coordinates[0][1],
                  longitude: poiFeature.geometry.coordinates[0][0],
                };
              } else {
                // Fallback to click location
                coordinates = {
                  latitude: e.lngLat.lat,
                  longitude: e.lngLat.lng,
                };
              }
              
              const placeName = poiFeature.properties?.name || 
                               poiFeature.properties?.place_name || 
                               poiFeature.properties?.text ||
                               poiFeature.properties?.maki ||
                               'Unknown Place';
              
              console.log('[MapView] Clicked on map feature:', placeName, coordinates);
              
              // Create a temporary pin from the clicked feature
              if (onPinPress) {
                const tempPin: MapPin = {
                  id: `map-feature-${Date.now()}`,
                  coordinates,
                  title: placeName,
                  type: 'search',
                  subtitle: poiFeature.properties?.address || 
                           poiFeature.properties?.category || 
                           poiFeature.properties?.type ||
                           undefined,
                };
                console.log('[MapView] Calling onPinPress with temp pin:', tempPin);
                onPinPress(tempPin);
              } else {
                console.warn('[MapView] No onPinPress handler available');
              }
              
              // Prevent default map click behavior
              e.preventDefault();
              return;
            }
            
            if (isAggressiveMapboxCostMode()) {
              if (__DEV__) {
                console.log('[MapView] Reverse geocode fallback disabled by aggressive cost mode');
              }
              e.preventDefault();
              return;
            }

            // Don't call onMapPress here - no reverse geocode fallback in cost-first mode.
            e.preventDefault();
          });

          mapRef.current = map;
          
          // Fallback: if load event doesn't fire within 3 seconds, mark as loaded anyway
          // This handles cases where the map renders but the load event is delayed
          setTimeout(() => {
            if (mapRef.current && !mapLoaded) {
              if (__DEV__ && Platform.OS === 'web') {
                console.log('[MapView] Map load timeout - marking as loaded');
              }
              setMapLoaded(true);
            }
          }, 3000);
        } catch (error) {
          console.error('[MapView] Error initializing web map:', error);
          const userMessage = getUserFriendlyErrorMessage(error);
          setMapboxError(userMessage || 'Failed to initialize map. Please try refreshing the page.');
        }
      };

      // Initialize immediately since container is ready
      checkAndInit();
    };

    loadMapbox();

    return () => {
      // Cleanup: Remove all markers and popups
      if (Platform.OS === 'web') {
        markersRef.current.forEach(marker => {
          try {
            marker.remove();
          } catch (error) {
            // Ignore errors during cleanup
          }
        });
        markersRef.current = [];
        
        popupsRef.current.forEach(popup => {
          try {
            popup.remove();
          } catch (error) {
            // Ignore errors during cleanup
          }
        });
        popupsRef.current = [];
      }
      
      // Remove map instance
      if (mapRef.current) {
        try {
          mapRef.current.remove();
        } catch (error) {
          // Ignore errors during cleanup
        }
        mapRef.current = null;
        setMapLoaded(false);
      }
      initAttemptedRef.current = false;
      setContainerReady(false);
      
      // Clean up ResizeObserver
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
    };
  }, [containerReady]); // Re-run when container becomes ready

  // Update web map center (avoid tying it to zoom updates; that fights user panning/zooming)
  const lastCenteredRef = useRef<string | null>(null);
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (!mapRef.current || !mapLoaded || !center) return;
    if (center.latitude === 0 && center.longitude === 0) return;

    const key = `${center.latitude.toFixed(6)},${center.longitude.toFixed(6)}`;
    if (lastCenteredRef.current === key) return;
    lastCenteredRef.current = key;

    try {
      suppressZoomEventsUntilRef.current = Date.now() + PROGRAMMATIC_ZOOM_SUPPRESS_MS;
      mapRef.current.flyTo({
        center: [center.longitude, center.latitude],
        // Preserve current zoom to avoid “snap back” during zooming/panning.
        zoom: mapRef.current.getZoom?.() ?? zoomLevel,
        duration: 800,
      });
    } catch {
      // ignore
    }
  }, [center, mapLoaded, zoomLevel]);
  
  // Also update center when pins are added (even if map just loaded)
  // This ensures map centers on trip pins, not user location (prefer itinerary/landmark over user pin).
  useEffect(() => {
    if (Platform.OS === 'web' && mapRef.current && mapLoaded && pinsForRender.length > 0 && !calloutPinId) {
      const preferredPin =
        pinsForRender.find((p) => p.type === 'itinerary') ||
        pinsForRender.find((p) => p.type === 'landmark') ||
        pinsForRender.find((p) => p.type === 'search') ||
        pinsForRender[0];
      const firstPin = preferredPin;
      if (firstPin) {
        // Check if current map center is far from the pin (more than 1 degree = ~111km)
        const currentCenter = mapRef.current.getCenter();
        const latDiff = Math.abs(currentCenter.lat - firstPin.coordinates.latitude);
        const lngDiff = Math.abs(currentCenter.lng - firstPin.coordinates.longitude);
        
        // If center is default (0, 0) or old default (20, 0) or far from pin, re-center
        if ((center && (center.latitude === 0 && center.longitude === 0)) ||
            (center && center.latitude === 20 && center.longitude === 0) || 
            latDiff > 1 || lngDiff > 1) {
          if (__DEV__) {
            console.log(`[MapView] Re-centering on first pin: ${firstPin.coordinates.latitude}, ${firstPin.coordinates.longitude}`);
          }
          suppressZoomEventsUntilRef.current = Date.now() + PROGRAMMATIC_ZOOM_SUPPRESS_MS;
          mapRef.current.flyTo({
            center: [firstPin.coordinates.longitude, firstPin.coordinates.latitude],
            zoom: mapRef.current.getZoom?.() ?? zoomLevel,
            duration: 800,
          });
        }
      }
    }
  }, [pinsForRender, mapLoaded, center, calloutPinId, zoomLevel]);

  // Web map container ref - improved reliability
  // Use a callback ref that directly receives the DOM element
  const setContainerRef = useCallback((viewRef: any) => {
    if (Platform.OS !== 'web') {
      return;
    }

    // If ref is null, clear the container
    if (!viewRef) {
      // Clean up ResizeObserver if it exists
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
      mapContainerRef.current = null;
      setContainerReady(false);
      return;
    }

    // Try multiple methods to get the DOM element (React Native Web compatibility)
    let domElement: HTMLElement | null = null;
    
    // Method 1: Direct ref (React Native Web v0.19+)
    if (viewRef && viewRef.nodeName === 'DIV') {
      domElement = viewRef;
    }
    // Method 2: _nativeNode (older React Native Web)
    else if ((viewRef as any)?._nativeNode) {
      domElement = (viewRef as any)._nativeNode;
    }
    // Method 3: node property
    else if ((viewRef as any)?.node) {
      domElement = (viewRef as any).node;
    }
    // Method 4: Try as-is if it's already an HTMLElement
    else if (viewRef && typeof viewRef === 'object' && 'tagName' in viewRef) {
      domElement = viewRef as HTMLElement;
    }
    
    if (domElement && (domElement.nodeName === 'DIV' || domElement.tagName === 'DIV')) {
      mapContainerRef.current = domElement as HTMLDivElement;
      // Mark it for easy identification
      mapContainerRef.current.setAttribute('data-map-container', 'true');
      
      // Immediately set basic styles to ensure container is usable
      const container = mapContainerRef.current;
      const computedStyle = window.getComputedStyle(container);
      const parent = container.parentElement;
      
      // Force minimum dimensions immediately - don't wait
      if (!computedStyle.width || computedStyle.width === '0px' || computedStyle.width === 'auto') {
        container.style.width = '100%';
      }
      if (!computedStyle.height || computedStyle.height === '0px' || computedStyle.height === 'auto') {
        const parentHeight = parent?.clientHeight || window.innerHeight || 300;
        container.style.height = parentHeight > 0 ? `${parentHeight}px` : '100%';
      }
      if (computedStyle.position === 'static') {
        container.style.position = 'relative';
      }
      
      // Mark as ready immediately - Mapbox can handle percentage dimensions
      // We'll verify dimensions are valid, but don't block initialization
      const checkAndSetReady = () => {
        if (!mapContainerRef.current) return false;
        
        const finalWidth = mapContainerRef.current.offsetWidth || mapContainerRef.current.clientWidth;
        const finalHeight = mapContainerRef.current.offsetHeight || mapContainerRef.current.clientHeight;
        
        // Be more lenient - if we have any dimensions, proceed
        if (finalWidth > 0 && finalHeight > 0) {
          setContainerReady(true);
          return true;
        }
        // Even if dimensions are 0, if we have percentage-based styles, proceed
        if (container.style.width.includes('%') || container.style.height.includes('%')) {
          setContainerReady(true);
          return true;
        }
        // If we have explicit pixel dimensions set, proceed
        if (container.style.width && container.style.width !== 'auto' && 
            container.style.height && container.style.height !== 'auto') {
          setContainerReady(true);
          return true;
        }
        return false;
      };
      
      // Try immediately - be aggressive
      if (checkAndSetReady()) {
        return; // Container is ready, initialization will proceed
      }
      
      // Force ready immediately if we have the DOM element and styles set
      // Mapbox GL JS can handle containers that resize later
      setContainerReady(true);
      
      // Use ResizeObserver for immediate detection
      if (typeof ResizeObserver !== 'undefined' && mapContainerRef.current) {
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect();
        }
        
        const resizeObserver = new ResizeObserver((entries) => {
          for (const entry of entries) {
            const { width, height } = entry.contentRect;
            if ((width > 0 && height > 0) && mapContainerRef.current) {
              setContainerReady(true);
              if (resizeObserverRef.current) {
                resizeObserverRef.current.disconnect();
                resizeObserverRef.current = null;
              }
            }
          }
        });
        resizeObserver.observe(mapContainerRef.current);
        resizeObserverRef.current = resizeObserver;
        
        setTimeout(() => {
          if (resizeObserverRef.current) {
            resizeObserverRef.current.disconnect();
            resizeObserverRef.current = null;
          }
        }, 1000); // Reduced timeout
      }
      
      // Fast fallback: mark as ready after very short delays
      requestAnimationFrame(() => {
        if (!containerReady && checkAndSetReady()) {
          // Success
        }
      });
      
      setTimeout(() => {
        if (!containerReady && checkAndSetReady()) {
          // Success
        }
      }, 16);
      
      // Additional check after a frame - but we already set it above
      // This is just a safety net
      requestAnimationFrame(() => {
        if (mapContainerRef.current && !containerReady) {
          setContainerReady(true);
        }
      });
    }
  }, [containerReady]);

  // Check if Mapbox is configured (only on native platforms)
  useEffect(() => {
    if (Platform.OS === 'web' || !Mapbox) {
      return;
    }
    
    const checkConfig = async () => {
      try {
        const { getMapboxAccessTokenSync } = require('@/config/mapbox');
        getMapboxAccessTokenSync();
        setMapboxError(null);
      } catch (error) {
        setMapboxError(
          'Mapbox access token not configured. Please set EXPO_PUBLIC_MAPBOX_ACCESS_TOKEN in .env file or configure Firebase Remote Config.'
        );
      }
    };
    checkConfig();
  }, []);

  // NOTE: native centering is handled by the unified center/zoom effect above.

  // Imperative zoom adjustments (used by in-app +/- controls)
  useEffect(() => {
    if (!zoomAdjustment) return;
    if (zoomCommandNonceRef.current === zoomAdjustment.nonce) return;
    zoomCommandNonceRef.current = zoomAdjustment.nonce;

    const nextZoomFrom = () => {
      if (Platform.OS === 'web' && mapRef.current?.getZoom) {
        return mapRef.current.getZoom();
      }
      return zoomRef.current ?? zoomLevel;
    };

    const current = nextZoomFrom();
    const nextZoom = Math.max(minZoomLevel, current + zoomAdjustment.delta);

    if (Platform.OS === 'web') {
      const map = mapRef.current;
      if (map) {
        map.easeTo({ zoom: nextZoom, duration: 320 });
      }
    } else if (cameraRef.current) {
      suppressZoomEventsUntilRef.current = Date.now() + 400;
      cameraRef.current.setCamera({
        zoomLevel: nextZoom,
        animationDuration: 320,
      });
    }

    zoomRef.current = nextZoom;
    onZoomChange?.(nextZoom);
  }, [zoomAdjustment, onZoomChange, zoomLevel, minZoomLevel]);

  const getPinColor = (pin: MapPin): string => {
    if (pin.highlight === 'must_see') return '#E67E22';
    if (pin.highlight === 'trending') return theme.colors.accent || '#8E44AD';
    if (pin.type === 'user') return theme.colors.primary;
    if (pin.type === 'landmark') return '#D946EF'; // Distinct magenta for landmarks
    if (pin.type === 'search') return '#9B59B6';
    
    // Itinerary pins by category
    if (pin.category === 'flight') return '#3498DB'; // Blue
    if (pin.category === 'hotel') return '#2ECC71'; // Green
    if (pin.category === 'activity') return '#F39C12'; // Orange
    return '#95A5A6'; // Gray for 'other'
  };

  const handlePinPress = useCallback((pin: MapPin) => {
    if (onPinPress) {
      onPinPress(pin);
    }
  }, [onPinPress]);

  const getMarkerSizeForPin = useCallback(
    (pin: MapPin): 'small' | 'medium' | 'large' => {
      const hasIndexChip = showIndexChips && pin.index !== undefined;
      const isMustSeeLandmark =
        pin.type === 'landmark' &&
        (pin.highlight === 'must_see' || (Array.isArray(pin.badges) && pin.badges.includes('Must-see')));
      if (hasIndexChip || isMustSeeLandmark) {
        return 'large';
      }
      return 'medium';
    },
    [showIndexChips],
  );

  // Update map style when it changes (web only)
  useEffect(() => {
    if (Platform.OS === 'web' && mapRef.current && mapLoaded && mapStyle && styleLoaded) {
      try {
        mapRef.current.setStyle(mapStyle);
        // Reset style loaded flag to track new style loading
        setStyleLoaded(false);
        mapRef.current.once('style.load', () => {
          setStyleLoaded(true);
        });
      } catch (error) {
        console.error('[MapView] Error updating map style:', error);
      }
    }
  }, [mapStyle, mapLoaded, styleLoaded]);

  // Web map: Update markers when pins change
  useEffect(() => {
    if (Platform.OS !== 'web' || !mapRef.current || !mapLoaded) {
      return;
    }

    const map = mapRef.current;
    let cancelled = false;
    
    // Store event listeners for cleanup
    const clickHandlers: Array<{ element: HTMLElement; handler: (e?: MouseEvent) => void }> = [];
    
    // Dynamically import mapboxgl for marker creation
    import('mapbox-gl').then((mapboxglModule) => {
      if (cancelled) return;
      
      const mapboxgl = mapboxglModule.default;
      
      // Remove existing markers and popups with proper cleanup
      markersRef.current.forEach(marker => {
        try {
          marker.remove();
        } catch (error) {
          // Ignore errors during cleanup
        }
      });
      markersRef.current = [];
      
      popupsRef.current.forEach(popup => {
        try {
          popup.remove();
        } catch (error) {
          // Ignore errors during cleanup
        }
      });
      popupsRef.current = [];

      // Add new markers with numbers and popups
      pinsForDrawOrder.forEach((pin) => {
        if (cancelled) return;
        
        const el = createWebMarkerElement(pin, getMarkerSizeForPin(pin));

        const marker = new mapboxgl.Marker({ element: el })
          .setLngLat([pin.coordinates.longitude, pin.coordinates.latitude])
          .addTo(map);

        // Create popup
        const popupContent = document.createElement('div');
        popupContent.style.padding = '8px';
        popupContent.style.minWidth = '120px';
        
        const titleEl = document.createElement('div');
        titleEl.textContent = pin.title;
        titleEl.style.fontWeight = 'bold';
        titleEl.style.fontSize = '14px';
        titleEl.style.marginBottom = pin.subtitle ? '4px' : '0';
        popupContent.appendChild(titleEl);
        
        if (pin.subtitle) {
          const subtitleEl = document.createElement('div');
          subtitleEl.textContent = pin.subtitle;
          subtitleEl.style.fontSize = '12px';
          subtitleEl.style.color = '#666';
          subtitleEl.style.marginBottom = '4px';
          popupContent.appendChild(subtitleEl);
        }

        if (pin.badges && pin.badges.length > 0) {
          const badgesContainer = document.createElement('div');
          badgesContainer.style.display = 'flex';
          badgesContainer.style.flexWrap = 'wrap';
          badgesContainer.style.gap = '4px';
          badgesContainer.style.marginBottom = '4px';
          pin.badges.forEach((badge) => {
            const badgeEl = document.createElement('span');
            badgeEl.textContent = badge;
            badgeEl.style.fontSize = '10px';
            badgeEl.style.padding = '2px 6px';
            badgeEl.style.borderRadius = '999px';
            badgeEl.style.backgroundColor = theme.colors.primary;
            badgeEl.style.color = '#fff';
            badgesContainer.appendChild(badgeEl);
          });
          popupContent.appendChild(badgesContainer);
        }
        
        const popup = new mapboxgl.Popup({ 
          offset: 25, 
          closeButton: true,
          closeOnClick: false, // Don't close on click, we handle it
        })
          .setDOMContent(popupContent);

        // Make marker more clickable - add pointer cursor and ensure it's interactive
        el.style.cursor = 'pointer';
        el.style.zIndex =
          pin.type === 'itinerary' ? '1300' : pin.type === 'user' || pin.type === 'search' ? '1200' : '1000';
        el.style.pointerEvents = 'auto'; // Ensure clicks work
        el.style.userSelect = 'none'; // Prevent text selection
        
        // Add click handler - handle both marker and popup clicks
        const clickHandler = (e?: MouseEvent) => {
          e?.stopPropagation(); // Prevent map click events
          e?.preventDefault(); // Prevent default behavior
          if (cancelled) return;
          
          console.log('[MapView] Pin clicked:', {
            title: pin.title,
            type: pin.type,
            id: pin.id,
            hasHandler: !!handlePinPress,
          });
          
          // Close popup if open (try/catch in case it's not attached)
          try {
            popup.remove();
          } catch (err) {
            // Popup might not be attached, that's okay
          }
          
          // Call the handler immediately
          if (handlePinPress) {
            handlePinPress(pin);
          } else {
            console.warn('[MapView] No handlePinPress handler available');
          }
        };
        
        // Attach click handler to marker element
        const markerElement = marker.getElement();
        markerElement.addEventListener('click', clickHandler);
        
        // Also add mousedown to ensure it captures the event
        markerElement.addEventListener('mousedown', (e) => {
          e.stopPropagation();
        });
        
        // Also make the popup clickable
        popupContent.style.cursor = 'pointer';
        popupContent.style.pointerEvents = 'auto';
        const popupClickHandler = (e?: MouseEvent) => {
          e?.stopPropagation();
          e?.preventDefault();
          if (!cancelled) {
            popup.remove();
            handlePinPress(pin);
          }
        };
        popupContent.addEventListener('click', popupClickHandler);
        
        // Store handlers for cleanup
        clickHandlers.push({ element: markerElement, handler: clickHandler });
        clickHandlers.push({ element: popupContent, handler: popupClickHandler });

        // Don't use setPopup - it interferes with our click handling
        // Instead, manually show popup on hover and handle clicks ourselves
        markerElement.addEventListener('mouseenter', () => {
          if (!cancelled && map) {
            popup.setLngLat([pin.coordinates.longitude, pin.coordinates.latitude]);
            popup.addTo(map);
          }
        });
        
        markerElement.addEventListener('mouseleave', () => {
          if (!cancelled) {
            popup.remove();
          }
        });
        
        markersRef.current.push(marker);
        popupsRef.current.push(popup);
      });
    }).catch((error) => {
      console.error('[MapView] Failed to load mapboxgl for markers:', error);
    });
    
    // Cleanup function
    return () => {
      cancelled = true;
      
      // Remove all event listeners
      clickHandlers.forEach(({ element, handler }) => {
        try {
          if (element && typeof element.removeEventListener === 'function') {
            element.removeEventListener('click', handler);
          }
        } catch (error) {
          // Ignore errors during cleanup
        }
      });
      
      // Remove all markers and popups
      markersRef.current.forEach(marker => {
        try {
          marker.remove();
        } catch (error) {
          // Ignore errors during cleanup
        }
      });
      markersRef.current = [];
      
      popupsRef.current.forEach(popup => {
        try {
          popup.remove();
        } catch (error) {
          // Ignore errors during cleanup
        }
      });
      popupsRef.current = [];
    };
  }, [pinsForDrawOrder, mapLoaded, theme.colors.surface, handlePinPress]);

  // Web map: Update route lines when routes change
  useEffect(() => {
    if (Platform.OS !== 'web' || !mapRef.current || !mapLoaded || !styleLoaded) {
      return;
    }

    const map = mapRef.current;
    let cancelled = false;
    
    // Track all route IDs for cleanup
    const routeIds = new Set<string>();

    // Remove existing route sources, layers, and labels
    // First, get all existing sources to clean up properly
    // Check if style is loaded before accessing it
    if (!map.isStyleLoaded()) {
      console.warn('[MapView] Style not loaded, skipping route update');
      return;
    }

    let existingSources: Record<string, any>;
    try {
      existingSources = map.getStyle().sources;
    } catch (error) {
      console.warn('[MapView] Error getting style, skipping route update:', error);
      return;
    }
    Object.keys(existingSources).forEach((sourceId) => {
      if (sourceId.includes('-route-') || sourceId.startsWith('route-')) {
        try {
          // Get all layers using this source
          // Check if style is still loaded
          if (!map.isStyleLoaded()) {
            return; // Skip this source if style not loaded
          }
          let style: any;
          try {
            style = map.getStyle();
          } catch (error) {
            console.warn('[MapView] Error getting style for cleanup:', error);
            return; // Skip this source on error
          }
          const layers = style.layers || [];
          layers.forEach((layer: any) => {
            if (layer.source === sourceId) {
              try {
                map.removeLayer(layer.id);
              } catch (error) {
                // Layer might already be removed
              }
            }
          });
          // Remove the source
          try {
            map.removeSource(sourceId);
          } catch (error) {
            // Source might already be removed
              }
        } catch (error) {
          // Ignore errors during cleanup
        }
      }
    });

    // Remove routes from current routes array
    routes.forEach((route) => {
      const lineLayerId = `${route.id}-line`;
      const labelLayerId = `${route.id}-label`;
      const labelSourceId = `${route.id}-label`;
      
      try {
        if (map.getLayer(lineLayerId)) {
          map.removeLayer(lineLayerId);
        }
        if (map.getLayer(labelLayerId)) {
          map.removeLayer(labelLayerId);
        }
        if (map.getSource(route.id)) {
          map.removeSource(route.id);
        }
        if (map.getSource(labelSourceId)) {
          map.removeSource(labelSourceId);
        }
      } catch (error) {
        // Ignore errors - source/layer might not exist
      }
    });

    // Add new route sources and layers
    routes.forEach((route) => {
      if (cancelled || route.coordinates.length < 2) return;

      const lineCoordinates = route.coordinates.map(coord => [
        coord.longitude,
        coord.latitude,
      ]);

      // Calculate midpoint for label placement
      const midpointIndex = Math.floor(lineCoordinates.length / 2);
      const midpoint = lineCoordinates[midpointIndex];

      try {
        map.addSource(route.id, {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: lineCoordinates,
            },
          },
        });
        routeIds.add(route.id);

        // Add route line
        map.addLayer({
          id: `${route.id}-line`,
          type: 'line',
          source: route.id,
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
          },
          paint: {
            'line-color': route.color || theme.colors.primary,
            'line-width': route.width || 3,
          },
        });

        // Add route label if distance or duration is provided
        if ((route.distance || route.duration) && midpoint) {
          const labelText = route.label || (() => {
            const parts: string[] = [];
            if (route.distance) {
              parts.push(`${route.distance.toFixed(1)} km`);
            }
            if (route.duration) {
              const hours = Math.floor(route.duration / 60);
              const minutes = route.duration % 60;
              if (hours > 0) {
                parts.push(`${hours}h ${minutes}m`);
              } else {
                parts.push(`${minutes}m`);
              }
            }
            return parts.join(' • ');
          })();

          map.addSource(`${route.id}-label`, {
            type: 'geojson',
            data: {
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: midpoint,
              },
              properties: {
                label: labelText,
              },
            },
          });
          routeIds.add(`${route.id}-label`);

          map.addLayer({
            id: `${route.id}-label`,
            type: 'symbol',
            source: `${route.id}-label`,
            layout: {
              'text-field': ['get', 'label'],
              'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
              'text-size': 12,
              'text-anchor': 'center',
              'text-offset': [0, 0],
            },
            paint: {
              'text-color': route.color || theme.colors.primary,
              'text-halo-color': '#FFFFFF',
              'text-halo-width': 2,
              'text-halo-blur': 1,
            },
          });
        }
      } catch (error) {
        console.error(`[MapView] Error adding route ${route.id}:`, error);
      }
    });
    
    // Cleanup function
    return () => {
      cancelled = true;
      
      // Only clean up if map and style are still available
      if (!mapRef.current || !mapRef.current.isStyleLoaded()) {
        return;
      }
      
      const map = mapRef.current;
      
      // Clean up all routes
      routeIds.forEach((routeId) => {
        try {
          const lineLayerId = `${routeId}-line`;
          const labelLayerId = `${routeId}-label`;
          const labelSourceId = `${routeId}-label`;
          
          if (map.getLayer(lineLayerId)) {
            map.removeLayer(lineLayerId);
          }
          if (map.getLayer(labelLayerId)) {
            map.removeLayer(labelLayerId);
          }
          if (map.getSource(routeId)) {
            map.removeSource(routeId);
          }
          if (map.getSource(labelSourceId)) {
            map.removeSource(labelSourceId);
          }
        } catch (error) {
          // Ignore errors during cleanup
        }
      });
    };
  }, [routes, mapLoaded, styleLoaded, theme.colors.primary]);

  // Fit bounds on web
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (!mapRef.current || !mapLoaded || !bounds) return;

    const { southWest, northEast } = bounds;
    const isValid = (c: LocationCoordinates) =>
      Number.isFinite(c?.latitude) &&
      Number.isFinite(c?.longitude) &&
      Math.abs(c.latitude) <= 90 &&
      Math.abs(c.longitude) <= 180;
    if (!isValid(southWest) || !isValid(northEast)) return;

    const paddingObj = typeof boundsPadding === 'number'
      ? { top: boundsPadding, right: boundsPadding, bottom: boundsPadding, left: boundsPadding }
      : boundsPadding;

    try {
      suppressZoomEventsUntilRef.current = Date.now() + FIT_BOUNDS_SUPPRESS_WEB_MS;
      mapRef.current.fitBounds(
        [southWest.longitude, southWest.latitude],
        [northEast.longitude, northEast.latitude],
        {
          padding: paddingObj,
          duration: boundsAnimationDurationMs,
        },
      );
    } catch (error) {
      console.warn('[MapView] fitBounds (web) failed:', error);
    }
  }, [bounds, boundsAnimationDurationMs, boundsPadding, mapLoaded, calloutPinId]);

  // Fit bounds on native
  useEffect(() => {
    if (Platform.OS === 'web') return;
    if (!Mapbox || !cameraRef.current || !bounds) return;

    const { southWest, northEast } = bounds;
    const isValid = (c: LocationCoordinates) =>
      Number.isFinite(c?.latitude) &&
      Number.isFinite(c?.longitude) &&
      Math.abs(c.latitude) <= 90 &&
      Math.abs(c.longitude) <= 180;
    if (!isValid(southWest) || !isValid(northEast)) return;

    const paddingObj = typeof boundsPadding === 'number'
      ? { top: boundsPadding, right: boundsPadding, bottom: boundsPadding, left: boundsPadding }
      : boundsPadding;

    try {
      suppressZoomEventsUntilRef.current = Date.now() + FIT_BOUNDS_SUPPRESS_NATIVE_MS;
      cameraRef.current.setCamera({
        bounds: {
          ne: [northEast.longitude, northEast.latitude],
          sw: [southWest.longitude, southWest.latitude],
          paddingTop: paddingObj.top,
          paddingRight: paddingObj.right,
          paddingBottom: paddingObj.bottom,
          paddingLeft: paddingObj.left,
        },
        animationDuration: boundsAnimationDurationMs,
      });
    } catch (error) {
      console.warn('[MapView] fitBounds (native) failed:', error);
    }
  }, [bounds, boundsAnimationDurationMs, boundsPadding, calloutPinId]);

  // Render error state with improved UI and retry option
  const renderErrorState = (errorMessage: string) => {
    const isTokenError = errorMessage.toLowerCase().includes('token') || 
                         errorMessage.toLowerCase().includes('not configured');
    const isNetworkError = errorMessage.toLowerCase().includes('network') ||
                          errorMessage.toLowerCase().includes('connection') ||
                          errorMessage.toLowerCase().includes('fetch');
    
    const handleRetry = () => {
      setMapboxError(null);
      // Force re-initialization
      if (Platform.OS === 'web' && mapContainerRef.current) {
        initAttemptedRef.current = false;
        // Trigger re-initialization by clearing and resetting
        if (mapRef.current) {
          try {
            mapRef.current.remove();
          } catch (e) {
            // Ignore cleanup errors
          }
          mapRef.current = null;
          setMapLoaded(false);
          setStyleLoaded(false);
        }
        // Re-run initialization
        setTimeout(() => {
          if (mapContainerRef.current) {
            // The useEffect will handle re-initialization
            setContainerReady(true);
          }
        }, 100);
      }
    };
    
    return (
      <View style={[styles.container, style, styles.errorContainer]}>
        <View style={styles.errorContent}>
          <View style={[styles.errorIconContainer, { backgroundColor: theme.colors.error + '15' }]}>
            <Ionicons 
              name="map-outline" 
              size={32} 
              color={theme.colors.error} 
            />
          </View>
          <Body 
            variant="bodyStrong" 
            style={[styles.errorTitle, { color: theme.colors.text }]}
          >
            {isTokenError ? 'Mapbox Token Not Configured' : 'Map Error'}
          </Body>
          <Caption 
            color={theme.colors.textSecondary} 
            style={styles.errorDescription}
          >
            {errorMessage}
          </Caption>
          <View style={styles.errorActions}>
            {isTokenError && (
              <Caption 
                color={theme.colors.primary} 
                style={styles.errorLink}
                onPress={() => {
                  // Show setup instructions
                  const instructions = 
                    '1. Create a .env file in the project root\n' +
                    '2. Add: EXPO_PUBLIC_MAPBOX_ACCESS_TOKEN=pk.your_token\n' +
                    '3. Restart the dev server\n\n' +
                    'See MAPBOX_SETUP.md for details.';
                  
                  if (Platform.OS === 'web') {
                    // On web, try to open the setup file or show alert
                    Alert.alert('Setup Instructions', instructions);
                  } else {
                    // On mobile, show alert with instructions
                    Alert.alert('Setup Instructions', instructions);
                  }
                }}
              >
                View Setup Instructions →
              </Caption>
            )}
            {isNetworkError && (
              <Caption 
                color={theme.colors.primary} 
                style={styles.errorLink}
                onPress={handleRetry}
              >
                Retry →
              </Caption>
            )}
          </View>
        </View>
      </View>
    );
  };

  // Show error for native platforms
  if (Platform.OS !== 'web' && (mapboxError || !Mapbox || !Camera || !PointAnnotation)) {
    return renderErrorState(mapboxError || 'Mapbox not available');
  }

  // Web map rendering
  if (Platform.OS === 'web') {
    if (mapboxError) {
      return renderErrorState(mapboxError);
    }

    // For web, ensure the container has explicit dimensions
    const webContainerStyle = Platform.OS === 'web' ? {
      width: '100%',
      height: '100%',
      minHeight: 300,
      minWidth: '100%',
      position: 'relative' as const,
      display: 'flex' as const,
      flex: 1,
    } : {};

    return (
      <View
        style={[
          styles.container,
          webContainerStyle,
          style,
        ]}
        ref={setContainerRef}
        // Add a data attribute for easier debugging
        {...(Platform.OS === 'web' ? { 'data-testid': 'map-container' } : {})}
      >
        {/* Show loading overlay only while map is initializing */}
        {!mapLoaded && !mapboxError && (
          <View style={styles.loadingOverlay}>
            <ActivityIndicator size="large" color={theme.colors.primary} />
            <Caption color={theme.colors.textSecondary} style={styles.loadingText}>
              Loading map...
            </Caption>
          </View>
        )}
        
        {/* Skeleton loading state for geocoding */}
        {mapLoaded && pins.length === 0 && center && (center.latitude === 0 && center.longitude === 0) && (
          <View style={styles.skeletonOverlay}>
            <View style={[styles.skeletonPulse, { backgroundColor: theme.colors.borderMuted }]} />
          </View>
        )}
      </View>
    );
  }

  const MapboxModule = Mapbox!;
  const CameraComponent = Camera!;
  const PointAnnotationComponent = PointAnnotation!;
  const MarkerViewComponent = MarkerView;
  const ShapeSourceComponent = ShapeSource;
  const LineLayerComponent = LineLayer;

  return (
    <View style={[styles.container, style]}>
      <MapboxModule.MapView
        styleURL={mapStyle || MAPBOX_STYLE_URL}
        style={styles.map}
        scaleBarEnabled={false}
        onPress={(feature: any) => {
          if (onMapPress && feature.geometry.coordinates) {
            onMapPress({
              latitude: feature.geometry.coordinates[1],
              longitude: feature.geometry.coordinates[0],
            });
          }
        }}
        onCameraChanged={(event: any) => {
          const zoomValue = event?.properties?.zoom;
          if (typeof zoomValue === 'number') {
            if (Date.now() < suppressZoomEventsUntilRef.current) return;
            zoomRef.current = zoomValue;
            const now = Date.now();
            const z = Number(zoomValue.toFixed(2));
            pendingZoomRef.current = z;

            const last = lastZoomEmitRef.current;
            const delta = Math.abs(z - last.z);

            // Big jumps (programmatic zoom) should update quickly.
            if (delta >= 0.6 && now - last.t > 80) {
              lastZoomEmitRef.current = { t: now, z };
              if (zoomEmitTimerRef.current) {
                clearTimeout(zoomEmitTimerRef.current);
                zoomEmitTimerRef.current = null;
              }
              onZoomChange?.(z);
              return;
            }

            // Debounce small zoom changes (pinch gestures) to avoid jank from upstream filtering/rerenders.
            if (zoomEmitTimerRef.current) {
              clearTimeout(zoomEmitTimerRef.current);
            }
            zoomEmitTimerRef.current = setTimeout(() => {
              zoomEmitTimerRef.current = null;
              if (Date.now() < suppressZoomEventsUntilRef.current) return;
              const next = pendingZoomRef.current;
              const prev = lastZoomEmitRef.current;
              if (Math.abs(next - prev.z) < 0.15 && Date.now() - prev.t < 600) return;
              lastZoomEmitRef.current = { t: Date.now(), z: next };
              onZoomChange?.(next);
            }, 320);
          }
        }}
      >
        <CameraComponent
          ref={cameraRef}
          defaultSettings={nativeDefaultCameraSettings}
          minZoomLevel={minZoomLevel}
        />

        {showNativeUserLocationDot && (
          <MapboxModule.UserLocation visible={true} />
        )}

        <React.Fragment key={pinsListKey}>
          {pinsForDrawOrder.map((pin) => {
            const shouldShowIndex = showIndexChips && pin.index !== undefined;
            const markerSize = getMarkerSizeForPin(pin);
            const coordinate: [number, number] = [pin.coordinates.longitude, pin.coordinates.latitude];

            // iOS Mapbox dev builds can fail to render Text inside PointAnnotation snapshots.
            // MarkerView renders a real React view and is much more reliable for numbered chips.
            const canUseMarkerView = Platform.OS === 'ios' && !!MarkerViewComponent && shouldShowIndex;

            if (canUseMarkerView) {
              const markerId = `${pin.id}-idx-${pin.index}`;
              return (
                <MarkerViewComponent
                  key={markerId}
                  id={markerId}
                  coordinate={coordinate}
                >
                  <MapMarker
                    pin={pin}
                    size={markerSize}
                    showIndex={true}
                    onPress={() => handlePinPress(pin)}
                  />
                </MarkerViewComponent>
              );
            }

            // Fallback: PointAnnotation
            const annotationId = shouldShowIndex ? `${pin.id}-idx-${pin.index}` : pin.id;
            return (
              <PointAnnotationComponent
                key={annotationId}
                id={annotationId}
                coordinate={coordinate}
                onSelected={() => handlePinPress(pin)}
              >
                <MapMarker
                  pin={pin}
                  size={markerSize}
                  showIndex={shouldShowIndex}
                />
              </PointAnnotationComponent>
            );
          })}
        </React.Fragment>

        {/* Route direction arrows (non-interactive) */}
        {routeArrows.map((a) => (
          <PointAnnotationComponent
            key={a.id}
            id={a.id}
            coordinate={[a.coordinate.longitude, a.coordinate.latitude]}
            onSelected={() => {}}
          >
            <View
              style={[
                styles.routeArrowContainer,
                {
                  transform: [{ rotate: `${a.bearingDeg}deg` }],
                },
              ]}
            >
              <Ionicons
                name="caret-up"
                size={a.size ?? 14}
                color={a.color || theme.colors.primary}
                style={styles.routeArrowIcon}
              />
            </View>
          </PointAnnotationComponent>
        ))}

        {/* Route lines */}
        {routes.map((route) => {
          if (!ShapeSourceComponent || !LineLayerComponent || route.coordinates.length < 2) {
            return null;
          }

          const lineCoordinates = route.coordinates.map(coord => [
            coord.longitude,
            coord.latitude,
          ]);

          return (
            <ShapeSourceComponent
              key={route.id}
              id={route.id}
              shape={{
                type: 'Feature',
                geometry: {
                  type: 'LineString',
                  coordinates: lineCoordinates,
                },
              }}
            >
              <LineLayerComponent
                id={`${route.id}-line`}
                style={{
                  lineColor: route.color || theme.colors.primary,
                  lineWidth: route.width || 3,
                  lineCap: 'round',
                  lineJoin: 'round',
                }}
              />
            </ShapeSourceComponent>
          );
        })}
      </MapboxModule.MapView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    ...(Platform.OS === 'web' ? {
      // Ensure web container has explicit dimensions
      minWidth: '100%',
      minHeight: 300,
    } : {}),
  },
  map: {
    flex: 1,
  },
  pin: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  pinNumbered: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 3,
  },
  pinNumber: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: 'bold',
  },
  errorContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
    backgroundColor: 'transparent',
  },
  errorContent: {
    alignItems: 'center',
    maxWidth: 400,
    gap: 12,
  },
  errorIconContainer: {
    width: 64,
    height: 64,
    borderRadius: 32,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  errorTitle: {
    fontSize: 18,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 4,
  },
  errorDescription: {
    textAlign: 'center',
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 8,
  },
  errorActions: {
    marginTop: 8,
  },
  errorLink: {
    fontSize: 14,
    fontWeight: '600',
    textDecorationLine: 'underline',
    ...(Platform.OS === 'web' ? ({ cursor: 'pointer' } as any) : {}),
  },
  errorText: {
    color: '#FF3B30',
    textAlign: 'center',
    fontSize: 14,
  },
  loadingContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    gap: 12,
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    zIndex: 1000,
    gap: 12,
  },
  loadingText: {
    marginTop: 8,
  },
  skeletonOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    zIndex: 999,
  },
  skeletonPulse: {
    width: 200,
    height: 200,
    borderRadius: 100,
    opacity: 0.3,
  },
  routeArrowContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  routeArrowIcon: {
    textShadowColor: 'rgba(0,0,0,0.35)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
});

